import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { MetadataItem } from '@gelato-api-ui/core/metadata/types/metadata-item';
import { EProductMetadataKey } from '@gelato-api-ui/core/e-commerce/e-product-metadata-key.enum';
import { EProductPublishRequestDefaultValues } from '@gelato-api-ui/core/e-commerce/e-product-publish-request-default-values';
import { MetadataService } from '@gelato-api-ui/core/metadata/services/metadata.service';
import { ECommerceProductsApiService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-products-api.service';
import { ECommerceProductPublishSettingsService } from '@api-ui-app/src/app/e-commerce-product-publishing/services/e-commerce-product-publish-settings.service';
import { ECommerceProductPublishingEventTrackingService } from '@api-ui-app/src/app/e-commerce-product-publishing/services/e-commerce-product-publishing-event-tracking.service';
import { EProductAbbridged } from '@gelato-api-ui/core/e-commerce/e-product-abbridged';
import { EProductPublishScope } from '@gelato-api-ui/core/e-commerce/e-product-publish-scope.enum';

@Injectable({ providedIn: 'root' })
export class ECommerceProductPublishingService {
  constructor(
    private readonly metadataService: MetadataService,
    private readonly eCommerceProductsApiService: ECommerceProductsApiService,
    private readonly eCommerceProductPublishingEventTrackingService: ECommerceProductPublishingEventTrackingService,
    private readonly eCommerceProductPublishSettingsService: ECommerceProductPublishSettingsService,
  ) {}

  publishProduct(productId: string, productMetadata: MetadataItem[]): Observable<EProductAbbridged> {
    const scopes = this.getScopes(productMetadata);
    const withFreeShipping = this.metadataService.getBooleanItemValue(
      productMetadata,
      EProductMetadataKey.PUBLISH_WITH_FREE_SHIPPING,
    );
    const isVisibleInTheOnlineStore = this.metadataService.getBooleanItemValue(
      productMetadata,
      EProductMetadataKey.PUBLISH_IMMEDIATELY,
    );

    this.eCommerceProductPublishingEventTrackingService.setProductMetadata(productMetadata);

    return this.eCommerceProductsApiService
      .publish(productId, {
        ...EProductPublishRequestDefaultValues,
        scopes,
        withFreeShipping,
        isVisibleInTheOnlineStore,
      })
      .pipe(
        take(1),
        catchError(error => {
          this.trackPublishError(productId, productMetadata, error?.response?.error);

          return of(null);
        }),
      );
  }

  private trackPublishError(productId: string, productMetadata: MetadataItem[], error: any) {
    const scopes = this.getScopes(productMetadata);
    const publishMode = this.eCommerceProductPublishSettingsService.getPublishModeFromMetadata(productMetadata);

    Sentry.captureException(new Error('eCommerceProductPublishFailed'), scope => {
      scope.setExtras({
        storeProductId: productId,
        scopes,
        publishMode,
        error,
      });

      scope.setTag('productPublishing', 'newProduct');

      return scope;
    });

    this.eCommerceProductPublishingEventTrackingService.trackPublishError(productId, error);
  }

  private getScopes(productMetadata: MetadataItem[]): EProductPublishScope[] {
    return this.eCommerceProductPublishSettingsService.getPublishScopesFromMetadata(productMetadata);
  }
}
